import React, { useState } from "react";
import { toastr } from "react-redux-toastr";
import Demographics from "./Demographics";
import WorkExperience from "./WorkExperience";
import { Customer, CivilIdDetails } from "../../../../core/api/models";
import Accordion from "../../../../components/Accordion";
import "../../../../styles/details.scss";
import api from "../../../../api";
import { ContainerLoader } from "../../../../components/loader";
import { useDataLoader } from "react-remote-data-hooks";
import { Entity, Lead, useHasPermission } from "../../../../core";
import { AppPermissions } from "../../../../core/api/models/permissions";
import ContactDetails from "./ContactDetails";

import { useAppSettings } from "../../../../core/hooks/useAppSettings";
import LeadEdit from "./LeadEdit";

const CustomerEdit: React.FunctionComponent<{
  onClose?: () => void;
  id: string;
  onReload?: (cus?: Customer) => void;
  entity: Entity[];
  leadDetails?: Lead;
}> = (props) => {
  const canUpdateCustomerDemographics = useHasPermission(
    AppPermissions.UpdateCustomerDemographics
  );
  const canUpdateCustomerExperiences = useHasPermission(
    AppPermissions.UpdateCustomerExperiences
  );
  const canEditCustomerSocialMediaDetails = useHasPermission(
    AppPermissions.CustomerSocialMediaDetails
  );
  const canUpdateCustomerPreferredTimes = useHasPermission(
    AppPermissions.UpdateCustomerPreferredTimes
  );
  const canUpdateVehicleUserDetails = useHasPermission(
    AppPermissions.UpdateVehicleUserDetails
  );
  const canUpdateCustomerDocuments = useHasPermission(
    AppPermissions.UpdateCustomerDocuments
  );
  const canUpdateCustomerPartnerInfo = useHasPermission(
    AppPermissions.UpdateCustomerPartnerInfo
  );

  const [isSubmiting, setIsSubmiting] = React.useState<boolean>(false);

  const topScrollRef = React.useRef<HTMLDivElement>(null);

  const [closeAllAccordion, setCloseAllAccordion] = useState<boolean>(false);
  const {
    data: customerDetails,
    loading,
    loaded,
    reload,
  } = useDataLoader<Customer>(() => api.customer.getDetails(props.id));

  const updateUser = async () => {
    const resp = await api.customer.getDetails(props.id);
    props?.onReload?.(resp);
  };

  const closeAccordion = () => {
    setCloseAllAccordion(true);
    topScrollRef?.current?.scrollIntoView();
  };

  const fields: {
    displayName: string;
    value: string;
    type: string;
    group: string;
  }[] = [];

  props?.entity?.forEach((en) => {
    en?.fields?.forEach((p) => {
      if (p?.type === "lead") {
        if (!(props?.leadDetails as any)?.[p.value]) {
          fields.push(p);
        }
      } else {
        if (p.group === "workDetails") {
          if (!(customerDetails?.workDetails?.[0] as any)?.[p.value]) {
            fields.push(p);
          }
        } else {
          if (p.value === "primaryEmail") {
            if (!customerDetails?.primaryEmail) {
              fields.push(p);
            }
          } else if (p.value === "identifiers.civilId") {
            if (!customerDetails?.identifiers?.civilId) {
              fields.push(p);
            }
          } else if (p.value === "identifiers.nationality") {
            if (!customerDetails?.identifiers?.nationality) {
              fields.push(p);
            }
          } else if (p.value === "addresses") {
            if (!customerDetails?.addresses?.length) {
              fields.push(p);
            }
          } else if (!(customerDetails as any)?.[p.value]) {
            fields.push(p);
          }
        }
      }
    });
  });

  const contactDetailsFields = fields?.filter(
    (x) => x.group === "contact-details"
  );

  const demographicsFields = fields?.filter((x) => x.group === "demographics");

  const workDetailsFields = fields?.filter((x) => x.group === "workDetails");

  const leadFields = fields?.filter((x) => x.type === "lead");

  const defaultId =
    !!leadFields?.length && props?.leadDetails
      ? "Lead Settings"
      : !!workDetailsFields?.length
      ? "Work Experience"
      : !!demographicsFields?.length
      ? "Demographics"
      : "ContactDetails";

  return (
    <>
      <div className="detail-poupup bg-white position-fixed top-0 w-100 h-100 overflow-auto in detail-poupup--xsm">
        <div className="container-fluid bg-gray-100 border-bottom py-3 position-sticky top-0 zIndex-9">
          <div className="row align-items-center">
            <div className="col-auto" onClick={props.onClose}>
              <i className="icon-close text-gray-500 font-xxl cursor-pointer"></i>
            </div>
            <div className="col d-flex"></div>
          </div>
        </div>

        <div className="container-fluid px-0 h-100">
          {customerDetails ? (
            <div className="row no-gutters h-100">
              <div className="col-md col-12  accordion">
                <Accordion
                  defaultId={defaultId}
                  onAccordionClick={() => setCloseAllAccordion(false)}
                  closeAll={closeAllAccordion}
                >
                  {!!contactDetailsFields?.length && (
                    <div id="ContactDetails">
                      <ContactDetails
                        customerDetails={customerDetails}
                        updateUser={() => {
                          updateUser();
                          closeAccordion();
                        }}
                        fields={contactDetailsFields}
                      ></ContactDetails>
                    </div>
                  )}
                  {!!demographicsFields?.length && (
                    <div id="Demographics">
                      <Demographics
                        customerDetails={customerDetails}
                        updateUser={() => {
                          updateUser();
                          closeAccordion();
                        }}
                        canUpdateCustomerDemographics={
                          canUpdateCustomerDemographics
                        }
                        fields={demographicsFields}
                      ></Demographics>
                    </div>
                  )}
                  {!!workDetailsFields?.length && (
                    <div id="Work Experience">
                      <WorkExperience
                        customerDetails={customerDetails}
                        updateUser={() => {
                          updateUser();
                          closeAccordion();
                        }}
                        canUpdateCustomerExperiences={
                          canUpdateCustomerExperiences
                        }
                        fields={workDetailsFields}
                      ></WorkExperience>
                    </div>
                  )}
                  {!!leadFields?.length && props?.leadDetails && (
                    <div id="Lead Settings">
                      <LeadEdit
                        onUpdate={() => {
                          closeAccordion();
                          props?.onReload?.();
                        }}
                        leadDetails={props.leadDetails}
                        fields={leadFields}
                      ></LeadEdit>
                    </div>
                  )}
                </Accordion>
              </div>
            </div>
          ) : (
            <ContainerLoader></ContainerLoader>
          )}
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
};

export default CustomerEdit;
